import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { firstValueFrom } from 'rxjs';
import { BranchesService, ClientBranch, QrContest, QrLevel, RegisteredQr } from '@services/branches.service';
import { SharedService } from '@services/shared.service';

@Component({
  selector: 'app-branches',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, NzModalModule, NzTableModule, NzSelectModule],
  templateUrl: './branches.component.html',
  styleUrls: ['./branches.component.scss'],
})
export class BranchesComponent implements OnInit {
  private branchesService = inject(BranchesService);
  private sharedService = inject(SharedService);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  isLoading = false;
  searchTerm = '';
  branches: ClientBranch[] = [];
  /** Set when the list request fails, so the card can explain it in place. */
  loadError = '';
  /** Company the branches belong to; the API needs it on both calls. */
  clientId = 0;

  // ── QR registration tab ──
  activeTab: 'branches' | 'qr' = 'branches';
  contests: QrContest[] = [];
  isLoadingContests = false;
  contestsError = '';
  selectedContestId = 0;
  selectedLevelId = 0;
  levels: QrLevel[] = [];
  isLoadingLevels = false;
  isRegistering = false;
  qrError = '';
  qrSuccess = '';

  // ── Registered QR levels list (getBranchByClient) ──
  registeredQrs: RegisteredQr[] = [];
  isLoadingRegisteredQrs = false;
  registeredQrsError = '';

  // Create / edit modal (one form, like the rest of the app)
  isFormModalVisible = false;
  isEditMode = false;
  isSaving = false;
  editingId = 0;
  branchNameInput = '';
  inlineError = '';

  // Delete confirmation
  isDeleteModalVisible = false;
  deletingBranch: ClientBranch | null = null;
  isDeleting = false;

  async ngOnInit(): Promise<void> {
    this.clientId = this.branchesService.getCurrentClientId();
    await this.loadBranches();
  }

  async loadBranches(): Promise<void> {
    this.isLoading = true;
    this.loadError = '';
    this.sharedService.showLoader();

    // Without a client id the endpoint would return every client's branches.
    if (!this.clientId) {
      this.isLoading = false;
      this.sharedService.hideLoader();
      this.branches = [];
      this.loadError = this.t('branches.no-client');
      this.cdr.detectChanges();
      return;
    }

    try {
      this.branches = await firstValueFrom(this.branchesService.getBranches(this.clientId));
    } catch (err: any) {
      console.warn('[BranchesComponent] Failed to load branches:', err);
      this.branches = [];
      // Shown inside the card with a Retry button. A modal on every page load
      // would block the screen for something the user cannot act on.
      this.loadError = this.extractError(err, this.t('branches.load-failed'));
    } finally {
      this.isLoading = false;
      this.sharedService.hideLoader();
      this.cdr.detectChanges();
    }
  }

  get filteredBranches(): ClientBranch[] {
    const term = (this.searchTerm || '').toLowerCase().trim();
    if (!term) return this.branches;
    return this.branches.filter(
      (b) => b.branchName.toLowerCase().includes(term) || String(b.id).includes(term)
    );
  }

  // ── Create / edit ─────────────────────────────────────────────
  openCreateModal(): void {
    this.isEditMode = false;
    this.editingId = 0;
    this.branchNameInput = '';
    this.inlineError = '';
    this.isFormModalVisible = true;
  }

  openEditModal(branch: ClientBranch, event?: MouseEvent): void {
    event?.stopPropagation();
    this.isEditMode = true;
    this.editingId = branch.id;
    this.branchNameInput = branch.branchName;
    this.inlineError = '';
    this.isFormModalVisible = true;
  }

  closeFormModal(): void {
    this.isFormModalVisible = false;
    this.inlineError = '';
  }

  onNameInput(): void {
    if (this.inlineError) this.inlineError = '';
  }

  get isFormValid(): boolean {
    return this.branchNameInput.trim().length >= 2;
  }

  async handleSave(): Promise<void> {
    const name = this.branchNameInput.trim();

    if (name.length < 2) {
      this.inlineError = this.t('branches.name-too-short');
      return;
    }
    // the row being edited is allowed to keep its own name
    const clash = this.branches.some(
      (b) => b.branchName.toLowerCase() === name.toLowerCase() && b.id !== this.editingId
    );
    if (clash) {
      this.inlineError = this.t('branches.name-duplicate');
      return;
    }

    this.isSaving = true;
    this.inlineError = '';
    try {
      if (this.isEditMode) {
        await firstValueFrom(this.branchesService.updateBranch(this.editingId, name, this.clientId));
        this.sharedService.createNotification('success', this.t('branches.updated'));
      } else {
        await firstValueFrom(this.branchesService.createBranch(name, this.clientId));
        this.sharedService.createNotification('success', this.t('branches.created'));
      }
      this.closeFormModal();
      await this.loadBranches();
    } catch (err: any) {
      console.warn('[BranchesComponent] save branch error:', err);
      this.inlineError = this.extractError(
        err,
        this.t(this.isEditMode ? 'branches.update-failed' : 'branches.create-failed')
      );
    } finally {
      this.isSaving = false;
      this.cdr.detectChanges();
    }
  }

  // ── Delete ────────────────────────────────────────────────────
  openDeleteModal(branch: ClientBranch, event?: MouseEvent): void {
    event?.stopPropagation();
    this.deletingBranch = branch;
    this.isDeleteModalVisible = true;
  }

  closeDeleteModal(): void {
    this.isDeleteModalVisible = false;
    this.deletingBranch = null;
  }

  async handleDelete(): Promise<void> {
    if (!this.deletingBranch) return;
    const branch = this.deletingBranch;
    this.isDeleting = true;
    try {
      await firstValueFrom(this.branchesService.deleteBranch(branch.id));
      this.sharedService.createNotification('success', this.t('branches.deleted'));
      this.closeDeleteModal();
      await this.loadBranches();
    } catch (err: any) {
      console.warn('[BranchesComponent] deleteBranch error:', err);
      this.sharedService.createNotification('error', this.extractError(err, this.t('branches.delete-failed')));
      this.closeDeleteModal();
    } finally {
      this.isDeleting = false;
      this.cdr.detectChanges();
    }
  }

  // ── QR registration ───────────────────────────────────────────
  async switchTab(tab: 'branches' | 'qr'): Promise<void> {
    this.activeTab = tab;
    if (tab !== 'qr') return;

    const needsContests = this.contests.length === 0 && !this.isLoadingContests;
    const needsRegistrations = this.registeredQrs.length === 0 && !this.isLoadingRegisteredQrs;
    if (!needsContests && !needsRegistrations) return;

    // Both loads sit behind one page loader: they are independent, so they run
    // in parallel, but a loader per card would appear and clear at its own
    // pace and read as two things going wrong.
    this.sharedService.showLoader();
    try {
      await Promise.all([
        needsContests ? this.loadContests(false) : Promise.resolve(),
        needsRegistrations ? this.loadRegisteredQrs(false) : Promise.resolve(),
      ]);
    } finally {
      this.sharedService.hideLoader();
    }
  }

  /**
   * The campaign a registration points at.
   *
   * my_registrations identifies it by UID only, so the name is looked up in
   * the campaigns list the form already loaded. Falls back to the UID when the
   * lookup cannot match it — better a raw id than an empty cell.
   */
  campaignLabelForUid(uid: string): string {
    if (!uid) return '-';
    const match = this.contests.find((c) => c.uid && c.uid === uid);
    return match ? match.name : uid;
  }

  /** Long UUIDs get shortened in the cell; the full value stays in the title. */
  shortUid(uid: string): string {
    if (!uid) return '-';
    return uid.length > 12 ? `${uid.slice(0, 8)}…${uid.slice(-4)}` : uid;
  }

  /** @param withLoader false when a caller already has the page loader up. */
  async loadRegisteredQrs(withLoader = true): Promise<void> {
    this.isLoadingRegisteredQrs = true;
    this.registeredQrsError = '';
    if (withLoader) this.sharedService.showLoader();
    try {
      this.registeredQrs = await firstValueFrom(this.branchesService.getRegisteredQrs());
    } catch (err: any) {
      console.warn('[BranchesComponent] Failed to load registered QRs:', err);
      this.registeredQrs = [];
      // Shown in the card with a Retry button, like the branches list does.
      this.registeredQrsError = this.extractError(err, this.t('branches.qr-list-failed'));
    } finally {
      this.isLoadingRegisteredQrs = false;
      if (withLoader) this.sharedService.hideLoader();
      this.cdr.detectChanges();
    }
  }

  /** @param withLoader false when a caller already has the page loader up. */
  async loadContests(withLoader = true): Promise<void> {
    this.isLoadingContests = true;
    this.contestsError = '';
    if (withLoader) this.sharedService.showLoader();
    try {
      this.contests = await firstValueFrom(this.branchesService.getContests());
    } catch (err: any) {
      console.warn('[BranchesComponent] Failed to load contests:', err);
      this.contests = [];
      this.contestsError = this.extractError(err, this.t('branches.qr-contests-failed'));
    } finally {
      this.isLoadingContests = false;
      if (withLoader) this.sharedService.hideLoader();
      this.cdr.detectChanges();
    }
  }

  /**
   * Changing the campaign always clears the level and reloads the level list,
   * because levels only arrive from the per-campaign endpoint.
   */
  async onContestChange(): Promise<void> {
    this.selectedLevelId = 0;
    this.levels = [];
    this.qrError = '';
    this.qrSuccess = '';

    const contestId = Number(this.selectedContestId);
    if (!contestId) return;

    this.isLoadingLevels = true;
    try {
      this.levels = await firstValueFrom(this.branchesService.getContestLevels(contestId));
    } catch (err: any) {
      console.warn('[BranchesComponent] Failed to load levels:', err);
      this.levels = [];
      this.qrError = this.extractError(err, this.t('branches.qr-levels-failed'));
    } finally {
      this.isLoadingLevels = false;
      this.cdr.detectChanges();
    }
  }

  onLevelChange(): void {
    this.qrError = '';
    this.qrSuccess = '';
  }

  /** A QR code only completes a level of this type. */
  isQrLevel(level: QrLevel): boolean {
    return level.levelType === 'client_qr_completion';
  }

  get canRegister(): boolean {
    return Number(this.selectedContestId) > 0 && Number(this.selectedLevelId) > 0 && !this.isRegistering;
  }

  async registerQr(): Promise<void> {
    if (!this.canRegister) return;

    this.isRegistering = true;
    this.qrError = '';
    this.qrSuccess = '';
    try {
      await firstValueFrom(
        this.branchesService.registerQr(Number(this.selectedContestId), Number(this.selectedLevelId))
      );
      this.qrSuccess = this.t('branches.qr-registered');
      this.sharedService.createNotification('success', this.qrSuccess);
      // The new registration belongs in the list below the form.
      await this.loadRegisteredQrs();
    } catch (err: any) {
      console.warn('[BranchesComponent] registerQr error:', err);
      this.qrError = this.extractError(err, this.t('branches.qr-failed'));
    } finally {
      this.isRegistering = false;
      this.cdr.detectChanges();
    }
  }

  private t(key: string): string {
    const value = this.translate.instant(key);
    return value === key ? key : value;
  }

  private extractError(err: any, fallback: string): string {
    return err?.error?.message || err?.error?.error || err?.message || fallback;
  }
}
